import { Component, computed, effect, inject, input, output } from "@angular/core";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanCustomPartNumbersComponent } from "@project/floorplanner/floorplan-custom-part-numbers/floorplan-custom-part-numbers.component";
import { FloorplanItemAttachmentsButtonComponent } from "@project/floorplanner/floorplan-item-attachments-button/floorplan-item-attachments-button.component";
import { FloorplanItemGroupComponent } from "@project/floorplanner/floorplan-item-group/floorplan-item-group.component";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import {
  buildTelemetryGroupedSourceItems,
  TelemetryGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getMonitoringConfigurationSummaryTitle,
  getTelemetryGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartSourceItem } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";

import { take } from "rxjs";

@Component({
  selector: "app-telemetry-notes",
  imports: [
    FloorplanItemDetailsComponent,
    FloorplanItemGroupComponent,
    FloorplanItemAttachmentsButtonComponent,
    FloorplanCustomPartNumbersComponent,
  ],
  templateUrl: "./telemetry-notes.component.html",
  styleUrl: "./telemetry-notes.component.scss",
  standalone: true,
})
export class TelemetryNotesComponent {
  readonly floorplanItem = input.required<FloorplanItem>();
  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly toggleExtendedButtonClicked = output<void>();
  protected readonly telemetry = computed(() => this.floorplanItem() as FloorplanTelemetry);
  protected readonly MonitoringCategory = MonitoringCategory;
  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  private readonly monitoringProductService = inject(MonitoringProductService);
  private readonly productService = inject(ProductService);

  protected productItems!: MonitoringProductItem[];
  protected monitoringItems: MonitoringGroupedPartSourceItem[] = [];
  protected imageUrl = "./assets/floorplan-menu-icons/telemetry_menu.svg";
  protected monitoringTitle = getMonitoringConfigurationSummaryTitle(MonitoringCategory.TELEMETRY);
  private readonly categoryLabels: TelemetryGroupedPartLabels = getTelemetryGroupedPartLabels();

  constructor() {
    effect(() => {
      const telemetry = this.telemetry();
      this.resolveDeviceImage(telemetry.config);
      this.productItems = telemetry.config.getOptions();
      this.monitoringItems = this.buildMonitoringItems(telemetry.config);
    });
  }

  private resolveDeviceImage(config: TelemetryConfiguration): void {
    const deviceId = config.family?.[0]?.option.devices[0];
    if (!deviceId) return;

    this.monitoringProductService
      .loadDeviceFromDB(deviceId, MonitoringCategory.TELEMETRY, this.productService.getFilterCountry())
      .pipe(take(1))
      .subscribe((device) => {
        const url = ProductImage.getCantoSourceUrl(device?.productImageCantoID ?? "");
        if (url) {
          this.imageUrl = url;
        }
      });
  }

  private buildMonitoringItems(config: TelemetryConfiguration): MonitoringGroupedPartSourceItem[] {
    return buildTelemetryGroupedSourceItems(
      {
        family: config.family,
        sensor: config.sensor,
        additionalBatteries: config.additionalBatteries,
      },
      this.categoryLabels,
    );
  }
}
